import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { useTranslations } from "@/providers/TranslationsProvider";
import { useLocalization } from "@/providers/useLocalization";
import type { ApplicationDocument } from "@/lib/userApplication";
import { DEFAULT_COUNTRY_BY_LANGUAGE, LICENCE_COUNTRIES, LICENCE_COUNTRY_NAMES, OTHER_COUNTRY, US_STATES, licenceCountryKey, licenceTypesFor, otherLicenceCountries } from "@/lib/licence";
import Dropdown from "@/components/Dropdown";
import FileUpload, { type UploadedFile } from "@/components/FileUpload";
import { keyFor } from "@/lib/translationHelpers";
import { Chip, Field, FieldMessage, GroupLegend, INPUT, SELECT, StepTitle } from "./fields";
import CountryFlag from "./CountryFlag";
import type { StepProps } from "./types";
const MAX_DOCUMENTS = 5;
// psychometrist's upload limit (config/upload.php max_size, 2048 KB).
const MAX_DOCUMENT_BYTES = 2 * 1024 * 1024;
const ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";
interface LicenceStepProps extends StepProps {
  documents: ApplicationDocument[];
  onDocumentsChange: (documents: ApplicationDocument[]) => void;
  onUnauthorized: () => void;
}
const documentUrl = (id: string) => `/api/v1/users/application/documents/${id}`;
export default function LicenceStep({
  draft,
  update,
  errors,
  documents,
  onDocumentsChange,
  onUnauthorized
}: Readonly<LicenceStepProps>) {
  const {
    t
  } = useTranslations();
  const {
    language,
    config
  } = useLocalization();
  const licence = draft.licence;
  const countryKey = licence.country ? licenceCountryKey(licence.country) : "";
  const types = countryKey ? licenceTypesFor(countryKey) : [];
  const type = types.find(x => x.id === licence.type);
  const setLicence = (patch: Partial<typeof licence>) => update({
    licence: {
      ...licence,
      ...patch
    }
  });
  // One list: the UI language's own country first (Iran for Persian, Kurdistan
  // for Kurdish, Germany for German...), then the other countries with their
  // own licence rules (Kurdistan first, Iran last), then every other country.
  const countryOptions = useMemo(() => [...[DEFAULT_COUNTRY_BY_LANGUAGE[language], ...LICENCE_COUNTRIES.filter(key => key !== DEFAULT_COUNTRY_BY_LANGUAGE[language])].filter(key => key !== OTHER_COUNTRY).map(key => ({
    value: key,
    label: t(`join.country.${key}`, LICENCE_COUNTRY_NAMES[key]),
    icon: <CountryFlag country={key} className="w-5 h-[14px]" />
  })), ...otherLicenceCountries(language)],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [language]);
  // t() echoes the key for a missing entry; optional hints just disappear.
  const hint = (key: string) => {
    const value = t(key);
    return value === key ? undefined : value;
  };
  const chooseCountry = (code: string) => {
    // A country without rules of its own gets the generic "other" licence.
    const nextTypes = licenceTypesFor(code);
    setLicence({
      country: code,
      type: nextTypes.length === 1 ? nextTypes[0].id : null
    });
  };
  const uploadDocument = async (file: File, signal: AbortSignal): Promise<UploadedFile> => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch("/api/v1/users/application/documents", {
      method: "POST",
      body: form,
      signal
    });
    if (res.status === 401) {
      onUnauthorized();
    }
    const data: {
      success?: boolean;
      return?: ApplicationDocument;
    } = await res.json().catch(() => ({}));
    if (!res.ok || !data.success || !data.return) {
      throw new Error(`upload failed: ${res.status}`);
    }
    const doc = data.return;
    onDocumentsChange([...documents, doc]);
    return {
      id: doc.id,
      url: documentUrl(doc.id),
      name: doc.name,
      type: doc.mime
    };
  };
  const removeDocument = async (file: UploadedFile) => {
    const res = await fetch(documentUrl(file.id), {
      method: "DELETE"
    });
    if (res.ok) {
      onDocumentsChange(documents.filter(d => d.id !== file.id));
    } else if (res.status === 401) {
      onUnauthorized();
    }
  };
  return <section>
      <StepTitle title={t("join.licence title", "Professional licence")} subtitle={t("join.licence subtitle", "We verify every doctor before they're listed. Your licence details are never shown to patients.")} />

      <div className="space-y-6">
        <div>
          <label id="join-licence-country" className="block mb-3 text-[15px] sm:text-[17px] tracking-[-0.022em] text-[var(--foreground)]">
            {t("join.licence country", "Where are you licensed?")}
          </label>
          <Dropdown searchable dir={config.direction} ariaLabel={t("join.licence country", "Where are you licensed?")} placeholder={t("join.choose country", "Choose a country")} value={licence.country ?? ""} options={countryOptions} onChange={chooseCountry} triggerClassName={`h-12 flex items-center rounded-full border-[1.5px] text-[16px] ${errors["licence.country"] ? "border-[#E30000] bg-[#FFF2F4] dark:bg-[#3A1D1D]" : "border-transparent bg-[#F2F2F7] dark:bg-[#2C2C2E] hover:bg-[#EBEBF0] dark:hover:bg-[#3A3A3C] focus-within:border-[#0071E3] focus-within:bg-white dark:focus-within:bg-[#1C1C1E]"} ps-5`} />
          <FieldMessage error={errors["licence.country"]} />
        </div>

        {types.length > 1 && <fieldset>
            <GroupLegend>{t("join.licence type", "Licence type")}</GroupLegend>
            <div className="flex flex-wrap gap-2">
              {types.map(x => <Chip key={x.id} selected={licence.type === x.id} onClick={() => setLicence({
            type: x.id
          })}>
                  {t(`join.licence types.${keyFor(x.id)}.label`, x.id)}
                </Chip>)}
            </div>
            <FieldMessage error={errors["licence.type"]} />
          </fieldset>}

        {type && <div className="space-y-4">
            <Field label={t(`join.licence types.${keyFor(type.id)}.number`, "Licence number")} hint={hint(`join.licence types.${keyFor(type.id)}.hint`)} error={errors["licence.number"]} filled={!!licence.number}>
              <input className={INPUT} dir="ltr" value={licence.number ?? ""} maxLength={60} aria-invalid={!!errors["licence.number"]} onChange={e => setLicence({
            number: e.target.value
          })} />
            </Field>

            {type.fields.includes("state") && <Field label={t("join.state", "Issuing state")} error={errors["licence.state"]} filled={!!licence.state} chevron>
                <select className={SELECT} value={licence.state ?? ""} aria-invalid={!!errors["licence.state"]} onChange={e => setLicence({
            state: e.target.value
          })}>
                  <option value="" />
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>}

            {type.fields.includes("npi") && <Field label={t("join.npi", "NPI")} hint={t("join.npi hint", "Your 10-digit National Provider Identifier")} error={errors["licence.npi"]} filled={!!licence.npi}>
                <input className={INPUT} dir="ltr" inputMode="numeric" value={licence.npi ?? ""} maxLength={20} aria-invalid={!!errors["licence.npi"]} onChange={e => setLicence({
            npi: e.target.value
          })} />
              </Field>}

            {type.fields.includes("issuer") && <Field label={t(`join.licence types.${keyFor(type.id)}.issuer`, t("join.issuer", "Issuing body"))} hint={hint(`join.licence types.${keyFor(type.id)}.issuer hint`)} error={errors["licence.issuer"]} filled={!!licence.issuer}>
                <input className={INPUT} value={licence.issuer ?? ""} maxLength={200} aria-invalid={!!errors["licence.issuer"]} onChange={e => setLicence({
            issuer: e.target.value
          })} />
              </Field>}

            {type.fields.includes("lanr") && <Field label={t("join.lanr", "LANR")} hint={t("join.lanr hint", "9 digits, only if you bill statutory health insurance")} error={errors["licence.lanr"]} optional optionalText={t("join.optional", "Optional")} filled={!!licence.lanr}>
                <input className={INPUT} dir="ltr" inputMode="numeric" value={licence.lanr ?? ""} maxLength={20} onChange={e => setLicence({
            lanr: e.target.value
          })} />
              </Field>}
          </div>}

        <div>
          <GroupLegendText optional={t("join.optional", "Optional")}>{t("join.documents", "Licence documents")}</GroupLegendText>
          <p className="mb-3 text-[13px] sm:text-[14px] leading-[1.43] text-[var(--foreground-secondary)] text-justify hyphens-auto">
            {t("join.documents hint", "A scan or photo of your licence speeds up verification. Only our review team can see it.")}
          </p>
          <FileUpload multiple accept={ACCEPT} maxFiles={MAX_DOCUMENTS} maxSize={MAX_DOCUMENT_BYTES} hint={t("join.upload limits", "PDF, PNG or JPG · up to 2 MB · max 5 files")} value={documents.map(d => ({
          id: d.id,
          url: documentUrl(d.id),
          name: d.name,
          type: d.mime
        }))} onUpload={uploadDocument} onRemove={removeDocument} />
        </div>
      </div>
    </section>;
}
function GroupLegendText(t0) {
  const $ = _c(5);
  const {
    children,
    optional
  } = t0;
  let t1;
  if ($[0] !== optional) {
    t1 = <span className="text-[#86868B]"> ({optional})</span>;
    $[0] = optional;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <h3 className="mb-1 text-[15px] sm:text-[17px] tracking-[-0.022em] text-[var(--foreground)]">{children}{t1}</h3>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}